import type {
  Application,
  BankMatch,
  BankProduct,
  DocumentItem,
  MatchReason,
  ProductType,
  RiskLevel,
  ScoringResult
} from "../types";

const round = (value: number, digits = 0) => {
  const multiplier = 10 ** digits;
  return Math.round(value * multiplier) / multiplier;
};

export const formatCurrency = (value: number) =>
  new Intl.NumberFormat("bg-BG", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0
  }).format(Number.isFinite(value) ? value : 0);

export const formatPercent = (value: number, digits = 1) =>
  `${round(value * 100, digits).toLocaleString("bg-BG", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits
  })}%`;

export const monthlyPayment = (principal: number, annualRate: number, years: number) => {
  if (principal <= 0 || years <= 0) return 0;
  const months = years * 12;
  const monthlyRate = annualRate / 100 / 12;
  if (monthlyRate === 0) return principal / months;
  return (principal * monthlyRate) / (1 - (1 + monthlyRate) ** -months);
};

const getProductType = (application: Application): ProductType => {
  if (application.financial.purpose === "consumer") return "consumer";
  if (application.financial.purpose === "business") return "business";
  if (application.financial.purpose === "refinance") return "refinance";
  return "mortgage";
};

const requiredDocuments = (application: Application): DocumentItem[] => {
  const base: DocumentItem[] = [
    { id: "id-card", label: "Лична карта", status: "missing", owner: "client" },
    { id: "income-proof", label: "Доказване на доход", status: "missing", owner: "client" }
  ];

  if (getProductType(application) === "mortgage") {
    base.push({
      id: "property-offer",
      label: "Документи за имота / предварителен договор",
      status: "missing",
      owner: "client"
    });
  }

  if (application.applicant.employmentType === "business_owner" || application.applicant.employmentType === "self_employed") {
    base.push({
      id: "tax-return",
      label: "Данъчна декларация / отчет за дейност",
      status: "missing",
      owner: "client"
    });
  }

  return base.filter((required) => {
    const actual = application.documents.find((document) => document.id === required.id);
    return !actual || actual.status !== "received";
  });
};

const riskFromScore = (score: number): RiskLevel => {
  if (score >= 78) return "low";
  if (score >= 62) return "medium";
  if (score >= 45) return "high";
  return "critical";
};

const reason = (label: string, tone: MatchReason["tone"]): MatchReason => ({ label, tone });

const evaluateBank = (
  application: Application,
  product: BankProduct,
  totalIncome: number,
  dti: number,
  ltv: number
): BankMatch => {
  const reasons: MatchReason[] = [];
  let fitScore = 72;
  let status: BankMatch["status"] = "match";
  const estimatedPayment = monthlyPayment(
    application.financial.requestedAmount,
    product.minRate,
    application.financial.termYears
  );

  if (product.productType !== getProductType(application)) {
    return {
      product,
      status: "blocked",
      fitScore: 0,
      estimatedPayment,
      reasons: [reason("Продуктът е за друг тип кредит.", "negative")]
    };
  }

  if (application.financial.requestedAmount > product.maxAmount) {
    status = "blocked";
    fitScore -= 35;
    reasons.push(reason(`Исканата сума надвишава лимита ${formatCurrency(product.maxAmount)}.`, "negative"));
  } else {
    fitScore += 8;
    reasons.push(reason("Сумата е в продуктовия лимит.", "positive"));
  }

  if (application.financial.termYears > product.maxTermYears) {
    status = "blocked";
    fitScore -= 25;
    reasons.push(reason(`Срокът е над максималните ${product.maxTermYears} г.`, "negative"));
  } else {
    reasons.push(reason("Срокът е допустим.", "positive"));
  }

  if (product.productType === "mortgage" && ltv > product.maxLtv) {
    status = status === "blocked" ? "blocked" : "review";
    fitScore -= 28;
    reasons.push(reason(`LTV ${formatPercent(ltv)} е над банковия праг ${formatPercent(product.maxLtv)}.`, "negative"));
  } else if (product.productType === "mortgage") {
    fitScore += 10;
    reasons.push(reason(`LTV ${formatPercent(ltv)} е в допустимия праг.`, "positive"));
  }

  if (dti > product.maxDti) {
    status = status === "blocked" ? "blocked" : "review";
    fitScore -= 26;
    reasons.push(reason(`DTI ${formatPercent(dti)} е над вътрешния праг ${formatPercent(product.maxDti)}.`, "warning"));
  } else {
    fitScore += 12;
    reasons.push(reason(`DTI ${formatPercent(dti)} е приемлив за продукта.`, "positive"));
  }

  if (totalIncome < product.minIncome) {
    status = status === "blocked" ? "blocked" : "review";
    fitScore -= 18;
    reasons.push(reason(`Доказаният доход е под ${formatCurrency(product.minIncome)}.`, "warning"));
  }

  if (application.applicant.employmentMonths < product.minEmploymentMonths) {
    status = status === "blocked" ? "blocked" : "review";
    fitScore -= 16;
    reasons.push(reason(`Заетостта е под ${product.minEmploymentMonths} месеца.`, "warning"));
  }

  if (!product.acceptedStages.includes(application.financial.constructionStage)) {
    status = status === "blocked" ? "blocked" : "review";
    fitScore -= 22;
    reasons.push(reason("Етапът на имота не е стандартно приет от продукта.", "warning"));
  }

  if (product.requiresSalaryTransfer && !application.applicant.salaryTransferPossible) {
    status = status === "blocked" ? "blocked" : "review";
    fitScore -= 10;
    reasons.push(reason("Най-добрите условия изискват превод на доход.", "warning"));
  }

  if (product.supportsOnlineFlow) {
    fitScore += 4;
    reasons.push(reason("Има дигитален или частично дигитален процес.", "positive"));
  }

  return {
    product,
    status,
    fitScore: Math.max(0, Math.min(100, Math.round(fitScore))),
    estimatedPayment,
    reasons
  };
};

export const scoreApplication = (application: Application, products: BankProduct[]): ScoringResult => {
  const activeProducts = products.filter((product) => product.isActive !== false);
  const totalIncome =
    application.financial.netMonthlyIncome +
    application.financial.coApplicantIncome +
    application.financial.otherMonthlyIncome;
  const type = getProductType(application);
  const referenceRate = type === "consumer" ? 6.2 : 3.1;
  const estimatedPayment = monthlyPayment(
    application.financial.requestedAmount,
    referenceRate,
    application.financial.termYears
  );
  const dti = totalIncome > 0 ? (application.financial.existingMonthlyDebt + estimatedPayment) / totalIncome : 1;
  const ltv =
    type === "mortgage" && application.financial.propertyValue > 0
      ? application.financial.requestedAmount / application.financial.propertyValue
      : 0;
  const childAdjustment = application.applicant.childrenUnder18 * 0.025;
  const maxAffordablePayment = Math.max(0, totalIncome * Math.max(0.34, 0.48 - childAdjustment) - application.financial.existingMonthlyDebt);
  const maxRecommendedAmount = maxAffordablePayment > 0
    ? round((maxAffordablePayment / Math.max(estimatedPayment, 1)) * application.financial.requestedAmount)
    : 0;

  let score = 100;
  const findings: MatchReason[] = [];

  if (dti > 0.6) {
    score -= 35;
    findings.push(reason("DTI е критично висок и не трябва да се изпраща автоматично към банка.", "negative"));
  } else if (dti > 0.5) {
    score -= 22;
    findings.push(reason("DTI е над 50% и изисква консултантски преглед.", "warning"));
  } else if (dti > 0.42) {
    score -= 10;
    findings.push(reason("DTI е граничен, но може да мине при правилна банка.", "warning"));
  } else {
    score += 3;
    findings.push(reason("DTI е в здравословен диапазон.", "positive"));
  }

  if (type === "mortgage" && ltv > 0.85) {
    score -= 28;
    findings.push(reason("LTV над 85% блокира автоматично насочване.", "negative"));
  } else if (type === "mortgage" && ltv > 0.8) {
    score -= 13;
    findings.push(reason("LTV е висок и ограничава броя банки.", "warning"));
  } else if (type === "mortgage") {
    score += 4;
    findings.push(reason("Самоучастието е достатъчно за няколко банки.", "positive"));
  }

  if (application.applicant.employmentMonths < 6) {
    score -= 24;
    findings.push(reason("Заетост под 6 месеца изисква ръчна проверка.", "warning"));
  } else if (application.applicant.employmentMonths >= 24) {
    score += 4;
    findings.push(reason("Стабилна трудова история.", "positive"));
  }

  if (!application.consents.precontractInfo || !application.consents.personalDataNotice || !application.consents.bankSharingConsent) {
    score -= 30;
    findings.push(reason("Липсват задължителни съгласия за обработка и споделяне.", "negative"));
  }

  if (application.financial.constructionStage === "act14") {
    score -= 10;
    findings.push(reason("Акт 14 трябва да мине през специален банков маршрут.", "warning"));
  }

  const missingDocuments = requiredDocuments(application);
  if (missingDocuments.length > 0) {
    score -= Math.min(20, missingDocuments.length * 6);
    findings.push(reason(`Липсват ${missingDocuments.length} документа преди банков пакет.`, "warning"));
  } else {
    score += 3;
    findings.push(reason("Минималният комплект документи е наличен.", "positive"));
  }

  const bankMatches = activeProducts
    .map((product) => evaluateBank(application, product, totalIncome, dti, ltv))
    .sort((left, right) => right.fitScore - left.fitScore);

  const normalizedScore = Math.max(0, Math.min(96, Math.round(score)));
  const risk = riskFromScore(normalizedScore);
  const strongMatches = bankMatches.filter((match) => match.status === "match" && match.fitScore >= 75);

  let autoRoute: ScoringResult["autoRoute"] = "consultant_review";
  if (risk === "critical" || (type === "mortgage" && ltv > 0.9)) {
    autoRoute = "stop";
  } else if (missingDocuments.length > 0) {
    autoRoute = "request_documents";
  } else if (strongMatches.length >= 2 && risk === "low") {
    autoRoute = "auto_to_bank";
  }

  return {
    score: normalizedScore,
    risk,
    totalIncome,
    estimatedPayment,
    disposableAfterDebt: totalIncome - application.financial.existingMonthlyDebt - estimatedPayment,
    dti,
    ltv,
    maxAffordablePayment,
    maxRecommendedAmount,
    autoRoute,
    findings,
    missingDocuments,
    bankMatches
  };
};

export const riskLabel: Record<RiskLevel, string> = {
  low: "Нисък",
  medium: "Среден",
  high: "Висок",
  critical: "Критичен"
};

export const routeLabel: Record<ScoringResult["autoRoute"], string> = {
  auto_to_bank: "Готово за автоматично насочване",
  consultant_review: "Консултантски преглед",
  request_documents: "Да се изискат документи",
  stop: "Да не се изпраща автоматично"
};
